import { useState } from "react";
import { Facebook, Instagram, Twitter, Linkedin, ChefHat, Utensils, Calendar, CreditCard, RefreshCw, XCircle, Users, Scale, BookOpen, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";

export default function Terms() {
  const [activeSection, setActiveSection] = useState("services");

  const sections = [
    { id: "services", title: "Services Offered", icon: <Utensils size={18} /> },
    { id: "payment", title: "Payment", icon: <CreditCard size={18} /> },
    { id: "refund", title: "Refund Policy", icon: <RefreshCw size={18} /> },
    { id: "cancellation", title: "Cancellation Policy", icon: <XCircle size={18} /> },
    { id: "third-parties", title: "Third Parties", icon: <Users size={18} /> },
    { id: "liability", title: "Limitation of Liability", icon: <Scale size={18} /> },
    { id: "governing-law", title: "Governing Law", icon: <BookOpen size={18} /> },
    { id: "changes", title: "Changes to Terms", icon: <Calendar size={18} /> },
    { id: "contact", title: "Contact Us", icon: <Mail size={18} /> }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-4 bg-green-100 rounded-full mb-6">
            <ChefHat className="text-green-600" size={36} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Please read these terms carefully before using our services. By accessing or using DailyFit services, you agree to be bound by these terms.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents */}
          <div className="lg:w-1/4">
            <div className="sticky top-24 bg-white rounded-xl shadow-md p-6">
              <h3 className="font-semibold text-lg mb-4 text-gray-900 flex items-center gap-2">
                <span>Contents</span>
              </h3>
              <nav>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left py-2 px-3 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeSection === section.id ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                      >
                        {section.icon}
                        <span>{section.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              {/* Section 1 */}
              <section id="services" className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Utensils className="text-blue-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">1. Services Offered</h2>
                </div>
                <p className="mb-4 text-gray-700">
                  <strong>1.1. DailyFit LLC</strong> operates as a healthy meal cloud kitchen providing services such as:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {[
                    "Fresh, ready-to-eat nutritious meals via delivery",
                    "Weekly and monthly meal plan subscriptions",
                    "Customized meal plans based on dietary needs",
                    "Calorie-counted and macro-balanced meal options",
                    "Corporate and office meal solutions",
                    "Family and kids' health-focused meal packages",
                    "Athlete and fitness-specific meal prep services",
                    "Detox programs and juice cleanses",
                    "Add-on wellness products like smoothies, snacks, and supplements"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-500 mr-2 mt-1">•</span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mb-3 text-gray-700">
                  1.2. All meals are prepared in our licensed kitchen and delivered in compliance with UAE food safety regulations.
                </p>
                <p className="text-gray-700">
                  1.3. By subscribing or placing an order, you agree to the terms outlined in this agreement. By purchasing our
                  services, you agree to comply with these terms and conditions.
                </p>
              </section>

              {/* Section 2 */}
              <section id="payment" className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CreditCard className="text-purple-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">2. Payment</h2>
                </div>
                <p className="mb-3 text-gray-700">
                  2.1. Payment for services must be made in full at the time of booking unless otherwise agreed upon.
                  Subscription-based services will be billed according to the plan selected (monthly, quarterly, or annually).
                </p>
                <p className="text-gray-700">
                  2.2. All prices are in AED (United Arab Emirates Dirham) and are subject to change without notice.
                </p>
              </section>

              {/* Section 3 */}
              <section id="refund" className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <RefreshCw className="text-amber-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">3. Refund Policy</h2>
                </div>
                <p className="mb-3 text-gray-700">3.1. Refunds will be provided under the following circumstances:</p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">•</span>
                    <span className="text-gray-700">If the service cannot be delivered as agreed upon due to unforeseen circumstances on our part.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">•</span>
                    <span className="text-gray-700">
                      If the service is canceled by the client at least 5 days before the scheduled session or subscription renewal,
                      subject to a cancellation fee of AED 50 to cover administrative costs.
                    </span>
                  </li>
                </ul>
                <p className="mb-3 text-gray-700">
                  3.2. Refund requests must be submitted in writing to{" "}
                  <a href="mailto:support@dailyfit.ae" className="text-green-600 underline hover:text-green-700 transition-colors">
                    support@dailyfit.ae
                  </a>{" "}
                  and will be processed within 7–10 business days.
                </p>
                <p className="mb-3 text-gray-700">
                  3.3. The total time for the refund to reflect in your account depends on your refund method and issuing bank.
                  We will refund the full amount of the product/service canceled, but the customer's bank may charge a transaction
                  fee as per their policy.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">•</span>
                    <span className="text-gray-700">If paid via credit/debit card, we will only refund the amount we received (excluding any gateway/bank charges).</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">•</span>
                    <span className="text-gray-700">
                      Refunds will be made via bank transfer if payment was made in cash. In such cases, customers must provide valid
                      bank account details to receive the refund.
                    </span>
                  </li>
                </ul>
              </section>

              {/* Section 4 */}
              <section id="cancellation" className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="text-red-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">4. Cancellation Policy</h2>
                </div>
                <p className="mb-3 text-gray-700">
                  4.1. Cancellations are subject to our approval. Clients may cancel a booked service by providing written notice to{" "}
                  <a href="mailto:support@dailyfit.ae" className="text-green-600 underline hover:text-green-700 transition-colors">
                    support@dailyfit.ae
                  </a>{" "}
                  at least 5 days prior to the scheduled date.
                </p>
                <p className="mb-3 text-gray-700">
                  4.2. Cancellation requests received after this period may be subject to a non-refundable cancellation fee of AED
                  50 to cover administrative and scheduling expenses.
                </p>
                <p className="mb-4 text-gray-700">4.3. A cancellation acknowledgment will be sent within 2 working days.</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">•</span>
                    <span className="text-gray-700">Cancellations made 5 days or more in advance of the service date will receive a 90% refund.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">•</span>
                    <span className="text-gray-700">Cancellations made within less than 5 days will incur a cancellation fee of AED 50.</span>
                  </li>
                </ul>
              </section>

              {/* Section 5 */}
              <section id="third-parties" className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Users className="text-indigo-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">5. Third Parties</h2>
                </div>
                <p className="text-gray-700">
                  5.1. Some of our services or features may be provided with the support of third-party service providers (e.g.,
                  payment gateways, content hosts, or fitness consultants). These third parties are bound by their own terms and
                  privacy policies, and DailyFit LLC is not liable for any issues arising from their services.
                </p>
              </section>

              {/* Section 6 */}
              <section id="liability" className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Scale className="text-gray-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">6. Limitation of Liability</h2>
                </div>
                <p className="mb-3 text-gray-700">
                  6.1. We shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in
                  connection with the provision of our services.
                </p>
                <p className="text-gray-700">
                  6.2. Our total liability to you for any claims arising out of or in connection with our services shall not exceed
                  the total amount paid by you for the relevant service.
                </p>
              </section>

              {/* Section 7 */}
              <section id="governing-law" className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BookOpen className="text-blue-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">7. Governing Law</h2>
                </div>
                <p className="text-gray-700">
                  7.1. These terms and conditions shall be governed by and construed in accordance with the laws of the United Arab
                  Emirates (UAE). Any disputes arising out of or in connection with these terms shall be subject to the exclusive
                  jurisdiction of the courts of Dubai, UAE.
                </p>
              </section>

              {/* Section 8 */}
              <section id="changes" className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Calendar className="text-amber-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">8. Changes to Terms and Conditions</h2>
                </div>
                <p className="text-gray-700">
                  8.1. We reserve the right to amend or update these terms and conditions at any time without prior notice. Your
                  continued use of our services after any such changes shall constitute your acceptance of the revised terms and
                  conditions.
                </p>
              </section>

              {/* Section 9 */}
              <section id="contact" className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Mail className="text-green-600" size={24} />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">9. Contact Us</h2>
                </div>
                <p className="mb-4 text-gray-700">
                  9.1. If you have any questions or concerns about these terms and conditions, please contact us:
                </p>
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <Mail className="text-green-600" size={20} />
                    <a href="mailto:support@dailyfit.ae" className="text-green-600 underline hover:text-green-700 transition-colors">
                      support@dailyfit.ae
                    </a>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <Phone className="text-green-600" size={20} />
                    <span className="text-gray-700">+971 562198110</span>
                  </div>
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="mt-8 text-center text-gray-500">
              <p className="mb-4">© {new Date().getFullYear()} DailyFit LLC. All rights reserved.</p>
              <div className="flex justify-center space-x-4">
                <a href="#" className="text-gray-500 hover:text-green-600 transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="#" className="text-gray-500 hover:text-green-600 transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="#" className="text-gray-500 hover:text-green-600 transition-colors">
                  <Twitter size={20} />
                </a>
                <a href="#" className="text-gray-500 hover:text-green-600 transition-colors">
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}